import { ACCESS_TOKEN_KEY, SocketEvent } from "@app/constants";
import { BootResult, RechargeResult } from "@app/types/socket-event.type";
import StorageService from "@core/services/storage";
import { Environment } from "@environments/environment";
import { Observable } from "rxjs";
import { io, ManagerOptions, Socket, SocketOptions } from "socket.io-client";

class _SocketService {
  private socket!: Socket;

  /** Phòng (account id) đã join, để join lại sau khi socket kết nối lại */
  private room?: string;

  /** Token đã gửi ở lần kết nối gần nhất: đăng nhập sau khi socket đã mở thì phải kết nối lại để gửi token */
  private connectedToken = "";

  public connect(options?: Partial<ManagerOptions & SocketOptions>) {
    const socketUrl = Environment.SOCKET_URL as string;

    const newOptions: Partial<ManagerOptions & SocketOptions> = {
      ...options,
    };

    if (!newOptions.transports || !newOptions.transports.length) {
      newOptions.transports = ["polling"];
    }

    // Gửi token ở mỗi lần (kết nối lại): API xác thực và tự cho socket vào phòng của tài khoản trong token
    if (!newOptions.auth) {
      newOptions.auth = (cb: (data: object) => void) => {
        this.connectedToken = StorageService.get(ACCESS_TOKEN_KEY) || "";
        cb({ token: this.connectedToken });
      };
    }

    if (!this.socket) {
      this.socket = io(socketUrl, newOptions);

      // Server mất phòng khi socket kết nối lại (đổi sid), nên join lại để vẫn nhận boot_result, recharge_result...
      this.socket.on("connect", () => {
        if (this.room) {
          this.socket.emit(SocketEvent.JOIN_ROOM, this.room);
        }
      });
    }
  }

  /**
   * Lắng nghe 1 sự kiện. Nếu socket chưa được tạo (component con chạy useEffect trước App) thì tạo luôn,
   * và gỡ listener khi unsubscribe để không bị cộng dồn khi vào lại trang.
   */
  private listen<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.connect();

      const handler = (data: T) => observer.next(data);

      this.socket.on(event, handler);

      return () => {
        this.socket.off(event, handler);
      };
    });
  }

  public onBootResult(): Observable<BootResult> {
    return this.listen<BootResult>(SocketEvent.BOOT_RESULT);
  }

  public onCreateVmResult(): Observable<any> {
    return this.listen<any>(SocketEvent.CREATE_VM_RESULT);
  }

  public onVmResult(): Observable<any> {
    return this.listen<any>(SocketEvent.VM_RESULT);
  }

  public onRechargeResult(): Observable<RechargeResult> {
    return this.listen<RechargeResult>(SocketEvent.RECHARGE_RESULT);
  }

  public emit(event: string, data: any) {
    if (event === SocketEvent.JOIN_ROOM) {
      this.room = data;

      // Socket mở trước khi đăng nhập (chưa có token) hoặc đã đổi tài khoản: kết nối lại để API nhận token mới
      const token = StorageService.get(ACCESS_TOKEN_KEY) || "";

      if (this.socket && token && token !== this.connectedToken) {
        this.socket.disconnect().connect();

        return;
      }
    }

    if (!this.socket) return;

    this.socket.emit(event, data);
  }

  public onErrorResponse(): Observable<string> {
    return this.listen<string>(SocketEvent.ERROR_RESPONSE);
  }

  public onVerifyPhoneResponse(): Observable<string> {
    return this.listen<string>(SocketEvent.VERIFY_PHONE_RESPONSE);
  }
}

const SocketService = new _SocketService();

export default SocketService;
